import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  BusinessOutlined as CorporateIcon,
  PersonOutlined as IndividualIcon,
  GroupsOutlined as EmployeeIcon,
  PublicOutlined as ThirdPartyIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapCompanyFromApi, isEtisalatEntityName } from '../../utils/apiMappers';
import {
  SHAREHOLDER_FORM_TYPES,
  THIRD_PARTY_SUBTYPES,
  formToApiShareholderType,
  apiToFormShareholderFields,
} from '../../utils/shareholderTypes';
import {
  parseShareholdingPercent,
  shareholdingPercentInputProps,
} from '../../utils/shareholdingPercent';

const SHAREHOLDER_TYPES = [
  { value: SHAREHOLDER_FORM_TYPES.GROUP_COMPANY, label: 'Group company', icon: CorporateIcon },
  { value: SHAREHOLDER_FORM_TYPES.THIRD_PARTY, label: 'Third party', icon: ThirdPartyIcon },
  { value: SHAREHOLDER_FORM_TYPES.INDIVIDUAL, label: 'Individual', icon: IndividualIcon },
  { value: SHAREHOLDER_FORM_TYPES.EMPLOYEE, label: 'Employee / management', icon: EmployeeIcon },
];

const THIRD_PARTY_OPTIONS = [
  { value: THIRD_PARTY_SUBTYPES.GOVERNMENT, label: 'Government' },
  { value: THIRD_PARTY_SUBTYPES.EXTERNAL, label: 'External organisation' },
  { value: THIRD_PARTY_SUBTYPES.PUBLIC, label: 'Public / stock exchange' },
];

const EMPTY_FORM = {
  shareholderType: '',
  thirdPartySubtype: THIRD_PARTY_SUBTYPES.GOVERNMENT,
  linkedCompanyId: '',
  shareholderName: '',
  country: '',
  percentage: '',
  shareCount: '',
  acquisitionDate: '',
  votingRights: true,
  isEtisalatEntity: false,
  notes: '',
};

const AddShareholderModal = ({
  open,
  onClose,
  onAdd,
  companyId,
  companyName,
  existingShareholders = [],
  editShareholder = null,
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editShareholder;
  const isGroupCompany = formData.shareholderType === SHAREHOLDER_FORM_TYPES.GROUP_COMPANY;
  const isThirdParty = formData.shareholderType === SHAREHOLDER_FORM_TYPES.THIRD_PARTY;
  const isIndividual = formData.shareholderType === SHAREHOLDER_FORM_TYPES.INDIVIDUAL;
  const isEmployee = formData.shareholderType === SHAREHOLDER_FORM_TYPES.EMPLOYEE;

  const currentTotal = existingShareholders
    .filter((sh) => !editShareholder || sh.id !== editShareholder.id)
    .reduce((sum, sh) => sum + (sh.percentage || 0), 0);

  const selectedRegistryCompany =
    companies.find((c) => String(c.id) === String(formData.linkedCompanyId)) || null;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    (async () => {
      setLoadingCompanies(true);
      try {
        const res = await csAdminApi.getCompanies();
        const list = (res.data?.data || [])
          .map((row) => {
            const mapped = mapCompanyFromApi(row);
            if (!mapped) return null;
            return {
              id: mapped.id,
              name: mapped.name,
              country: mapped.country || '',
            };
          })
          .filter(Boolean)
          .filter((c) => String(c.id) !== String(companyId))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setCompanies(list);
      } catch (err) {
        console.error('Load companies for shareholder picker:', err);
        if (!cancelled) setCompanies([]);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  useEffect(() => {
    if (!open) return;

    if (editShareholder) {
      const apiType = (editShareholder.shareholderType || '').toUpperCase();
      let formFields = apiToFormShareholderFields(editShareholder);

      // Legacy manual corporate entries that are not group entities → third party
      if (
        apiType === 'CORPORATE'
        && !editShareholder.isEtisalatEntity
        && !companies.some((c) => c.name === editShareholder.shareholderName)
      ) {
        formFields = {
          shareholderType: SHAREHOLDER_FORM_TYPES.THIRD_PARTY,
          thirdPartySubtype: THIRD_PARTY_SUBTYPES.EXTERNAL,
        };
      }

      setFormData({
        ...EMPTY_FORM,
        ...formFields,
        linkedCompanyId: '',
        shareholderName: editShareholder.shareholderName || '',
        country: editShareholder.nationality || '',
        percentage: editShareholder.percentage ?? '',
        shareCount: editShareholder.shareCount || '',
        acquisitionDate: editShareholder.acquisitionDate
          ? editShareholder.acquisitionDate.split('T')[0]
          : '',
        votingRights: editShareholder.votingRights ?? true,
        isEtisalatEntity: editShareholder.isEtisalatEntity ?? false,
        notes: editShareholder.notes || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setError(null);
  }, [editShareholder, open, companies]);

  useEffect(() => {
    if (!open || !editShareholder || formData.shareholderType !== SHAREHOLDER_FORM_TYPES.GROUP_COMPANY || !companies.length) {
      return;
    }

    const match = companies.find((c) => c.name === editShareholder.shareholderName);
    if (match) {
      setFormData((prev) => ({
        ...prev,
        linkedCompanyId: String(match.id),
        shareholderName: match.name,
        country: match.country || prev.country || editShareholder.nationality || '',
        isEtisalatEntity:
          editShareholder.isEtisalatEntity ?? isEtisalatEntityName(match.name),
      }));
    }
  }, [open, editShareholder, companies, formData.shareholderType]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTypeChange = (e) => {
    const shareholderType = e.target.value;
    setFormData({
      ...EMPTY_FORM,
      shareholderType,
      thirdPartySubtype: THIRD_PARTY_SUBTYPES.GOVERNMENT,
    });
    setError(null);
  };

  const handleRegistryCompanyChange = (_, company) => {
    if (!company) {
      setFormData((prev) => ({
        ...prev,
        linkedCompanyId: '',
        shareholderName: '',
        country: '',
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      linkedCompanyId: String(company.id),
      shareholderName: company.name,
      country: company.country || '',
      isEtisalatEntity: isEtisalatEntityName(company.name),
    }));
    setError(null);
  };

  const resolveShareholderName = () => {
    if (isGroupCompany) {
      return selectedRegistryCompany?.name || formData.shareholderName.trim();
    }
    return formData.shareholderName.trim();
  };

  const validateForm = () => {
    if (!formData.shareholderType) {
      setError('Please select a shareholder type');
      return false;
    }

    if (isGroupCompany && !formData.linkedCompanyId) {
      setError('Select a group company from the registry');
      return false;
    }

    if ((isThirdParty || isIndividual || isEmployee) && !formData.shareholderName.trim()) {
      setError('Shareholder name is required');
      return false;
    }

    const name = resolveShareholderName();
    if (!name) {
      setError('Shareholder name is required');
      return false;
    }

    if (!formData.percentage || Number(formData.percentage) <= 0) {
      setError('Please enter a valid percentage');
      return false;
    }
    if (Number(formData.percentage) > 100) {
      setError('Percentage cannot exceed 100%');
      return false;
    }

    const newTotal = currentTotal + parseFloat(formData.percentage);
    if (newTotal > 100) {
      setError(
        `Total shareholding would exceed 100% (current: ${currentTotal.toFixed(2)}%, adding: ${formData.percentage}%)`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      const apiType = formToApiShareholderType(formData);
      const shareholderData = {
        id: editShareholder?.id || Date.now(),
        shareholderName: resolveShareholderName(),
        shareholderType: apiType,
        nationality: formData.country.trim() || null,
        linkedCompanyId: isGroupCompany ? formData.linkedCompanyId : null,
        percentage: parseShareholdingPercent(formData.percentage),
        shareCount: formData.shareCount ? parseInt(formData.shareCount, 10) : null,
        acquisitionDate: formData.acquisitionDate || null,
        votingRights: formData.votingRights,
        isEtisalatEntity: isGroupCompany ? formData.isEtisalatEntity : false,
        notes: formData.notes.trim(),
      };

      if (onAdd) {
        await onAdd(shareholderData);
      }

      handleClose();
    } catch (err) {
      const message =
        err.response?.data?.error?.message
        || err.message
        || 'Failed to save shareholder. Please try again.';
      setError(message);
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setError(null);
    onClose();
  };

  const canSubmit =
    formData.shareholderType
    && formData.percentage
    && (isGroupCompany
      ? formData.linkedCompanyId
      : formData.shareholderName.trim());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Shareholder' : 'Add Shareholder'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Adding shareholder to:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {companyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current total: {currentTotal.toFixed(2)}% • Remaining: {(100 - currentTotal).toFixed(2)}%
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Shareholder Type</InputLabel>
            <Select
              value={formData.shareholderType}
              label="Shareholder Type"
              onChange={handleTypeChange}
              disabled={submitting}
            >
              {SHAREHOLDER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <type.icon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isThirdParty && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Third parties are outside your company registry — governments, public float, and other
              external owners. Enter a display name only; they are not added as companies.
            </Alert>
          )}

          {isGroupCompany && (
            <>
              <Autocomplete
                options={companies}
                loading={loadingCompanies}
                value={selectedRegistryCompany}
                onChange={handleRegistryCompanyChange}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                disabled={submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Group company"
                    placeholder="Search companies in the registry"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCompanies ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={handleChange('country')}
                disabled={submitting}
                placeholder="e.g. UAE"
                helperText={
                  selectedRegistryCompany
                    ? 'Pre-filled from the selected company; you can change it if needed'
                    : 'Country of incorporation'
                }
              />
            </>
          )}

          {isThirdParty && (
            <>
              <FormControl fullWidth required>
                <InputLabel>Third party category</InputLabel>
                <Select
                  value={formData.thirdPartySubtype}
                  label="Third party category"
                  onChange={handleChange('thirdPartySubtype')}
                  disabled={submitting}
                >
                  {THIRD_PARTY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label="Display name"
                value={formData.shareholderName}
                onChange={handleChange('shareholderName')}
                disabled={submitting}
                placeholder="e.g. Moroccan Government, Public / Stock Exchange"
              />
              <TextField
                fullWidth
                label="Country / jurisdiction (optional)"
                value={formData.country}
                onChange={handleChange('country')}
                disabled={submitting}
                placeholder="e.g. Morocco"
              />
            </>
          )}

          {(isIndividual || isEmployee) && (
            <>
              <TextField
                fullWidth
                required
                label={isIndividual ? 'Full name' : 'Employee / management name'}
                value={formData.shareholderName}
                onChange={handleChange('shareholderName')}
                disabled={submitting}
              />
              <TextField
                fullWidth
                label="Nationality / Country"
                value={formData.country}
                onChange={handleChange('country')}
                disabled={submitting}
                placeholder="e.g. UAE"
              />
            </>
          )}

          {formData.shareholderType && (
            <>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Ownership Percentage"
                  type="number"
                  value={formData.percentage}
                  onChange={handleChange('percentage')}
                  disabled={submitting}
                  inputProps={shareholdingPercentInputProps}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Number of Shares"
                  type="number"
                  value={formData.shareCount}
                  onChange={handleChange('shareCount')}
                  disabled={submitting}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label="Acquisition Date"
                type="date"
                value={formData.acquisitionDate}
                onChange={handleChange('acquisitionDate')}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.votingRights}
                    onChange={(e) => setFormData({ ...formData, votingRights: e.target.checked })}
                    disabled={submitting}
                  />
                }
                label="Has Voting Rights"
              />

              {isGroupCompany && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isEtisalatEntity}
                      onChange={(e) =>
                        setFormData({ ...formData, isEtisalatEntity: e.target.checked })
                      }
                      disabled={submitting}
                    />
                  }
                  label="Etisalat Group entity (counts toward Etisalat ownership)"
                />
              )}

              <TextField
                fullWidth
                label="Notes (optional)"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={2}
                placeholder="Additional information about this shareholder"
                disabled={submitting}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? 'Saving...' : isEditing ? 'Update Shareholder' : 'Add Shareholder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddShareholderModal;
